/* Clignote : pilotage Bluetooth des lunettes 24 × 9 (famille « Funky Glasses »).
   Écrit pour Clignote à partir de la documentation publique du protocole
   (github.com/MeioNeiz/joggles, notes/protocol.md). Aucun code recopié.

   Rappels du protocole :
   - service fff0, 4 caractéristiques d44bc439-…9600 (commandes), 9601 (réponses),
     960a (envoi en bloc DATS), 960b (temps réel : colonnes DIY)
   - chaque écriture = UN bloc de 16 octets chiffré en AES-128-ECB, clé fixe
   - trame en clair : [longueur][opcode ASCII][arguments][zéros jusqu'à 16]
   - écran : 9 lignes × 24 colonnes, ligne 0 = EN BAS, 2 bits par pixel (niveaux 0 à 3)
   - jamais plusieurs blocs dans une écriture, ~18 ms entre deux écritures */
(function (global) {
  const SERVICE = "0000fff0-0000-1000-8000-00805f9b34fb";
  const CH = {
    cmd: "d44bc439-abfd-45a2-b575-925416129600",
    notify: "d44bc439-abfd-45a2-b575-925416129601",
    dats: "d44bc439-abfd-45a2-b575-92541612960a",
    live: "d44bc439-abfd-45a2-b575-92541612960b",
  };
  const KEY_HEX = "34522a5b7a6e492c08090a9d8d2a23f8";
  const ROWS = 9, COLS = 24;
  // LED absentes (ligne appareil : [première colonne, colonne après la dernière])
  const DEAD = { 8: [9, 15], 1: [10, 14], 0: [9, 15] };
  const alive = (row, col) => { const g = DEAD[row]; return !g || col < g[0] || col >= g[1]; };

  const hex = (u8) => [...u8].map((b) => b.toString(16).padStart(2, "0")).join("");
  const fromHex = (h) => new Uint8Array(h.match(/../g).map((x) => parseInt(x, 16)));
  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  /* AES-128-ECB d'un bloc via WebCrypto : AES-CBC avec IV nul donne le même premier bloc. */
  let keyPromise = null;
  const subtle = () => (global.crypto && global.crypto.subtle) || null;
  function getKey() {
    if (!keyPromise) keyPromise = subtle().importKey("raw", fromHex(KEY_HEX), { name: "AES-CBC" }, false, ["encrypt"]);
    return keyPromise;
  }
  async function encryptBlock(block) {
    const out = await subtle().encrypt({ name: "AES-CBC", iv: new Uint8Array(16) }, await getKey(), block);
    return new Uint8Array(out).slice(0, 16);
  }
  /* Les réponses des lunettes sont chiffrées aussi. WebCrypto n'a pas d'ECB : on ajoute derrière le bloc C
     un 2e bloc C2 = E(remplissage XOR C). En CBC, le 2e bloc se déchiffre alors en remplissage valide,
     WebCrypto le retire et rend exactement D(C). */
  let decKeyPromise = null;
  async function decryptBlock(c) {
    if (!decKeyPromise) decKeyPromise = subtle().importKey("raw", fromHex(KEY_HEX), { name: "AES-CBC" }, false, ["decrypt"]);
    const pad = new Uint8Array(16).fill(16).map((v, i) => v ^ c[i]);
    const c2 = await encryptBlock(pad);
    const both = new Uint8Array(32); both.set(c, 0); both.set(c2, 16);
    const out = await subtle().decrypt({ name: "AES-CBC", iv: new Uint8Array(16) }, await decKeyPromise, both);
    return new Uint8Array(out).slice(0, 16);
  }

  /* Trame en clair */
  function frame(opcode, ...args) {
    const body = [...opcode].map((c) => c.charCodeAt(0)).concat(args.map((a) => a & 0xff));
    if (body.length > 15) throw new Error("trame trop longue");
    const out = new Uint8Array(16);
    out[0] = body.length; out.set(body, 1);
    return out;
  }
  const CMD = {
    enterDIY: () => frame("SMVEW", 1),
    exitDIY: () => frame("SMVEW", 0),
    saveDIY: () => frame("SMVEW", 2),
    light: (n) => frame("LIGHT", n),
    speed: (n) => frame("SPEED", n),
    ledOn: () => frame("LEDON"),
    ledOff: () => frame("LEDOFF"),
    invert: () => frame("EVERT"),
    anim: (n) => frame("ANIM", n),     // 20 à 29
    loopAnims: () => frame("LOOA"),
    image: (n) => frame("IMAG", n),    // 0 à 10
    mode: (m, d = 0) => frame("MODE", m, d), // 1 fixe, 2 défilement horizontal, 3 vertical
    exitRhythm: () => frame("SOUT"),
    dats: (type, len) => frame("DATS", type, (len >> 8) & 0xff, len & 0xff),
    datcp: () => frame("DATCP"),
    column: (i, w) => frame("", i, (w >> 16) & 0xff, (w >> 8) & 0xff, w & 0xff),
  };

  /* Image écran (y = 0 en haut, comme à l'écran) → mot de colonne appareil (ligne 0 en bas).
     Testé sur notre paire le 21/10/2026 : pas de niveaux de gris visibles, donc allumé (≥ 2) ou éteint. */
  function columnWord(px, c) {
    let bits = 0;
    for (let y = 0; y < ROWS; y++) {
      const r = ROWS - 1 - y;
      if ((px[y * COLS + c] | 0) >= 2 && alive(r, c)) bits |= 3 << (2 * r);
    }
    return bits;
  }
  /* Mode selfie : on retourne l'image gauche-droite (le masque des LED est symétrique). */
  const mirrorPx = (px) => px.map((_, i) => px[Math.floor(i / COLS) * COLS + (COLS - 1 - (i % COLS))]);
  /* Format texte DATS : 16 bits petit-boutiste par colonne, bits 0-6 = lignes 0-6. */
  function textPayload(cols7, flip, mirror) {
    if (mirror) cols7 = cols7.slice().reverse();
    const out = new Uint8Array(cols7.length * 2);
    cols7.forEach((c, i) => {
      let v = 0;
      for (let b = 0; b < 7; b++) if (c >> b & 1) v |= 1 << (flip ? 6 - b : b);
      out[i * 2] = v & 0x7f; out[i * 2 + 1] = 0;
    });
    return out;
  }

  class Glasses {
    constructor() {
      this.dev = null; this.ch = {}; this.pacing = 18; this.last = null; this.inDIY = false;
      this.queue = Promise.resolve(); this.replyWaiters = []; this.onstate = () => {}; this.onlog = () => {};
      this.state = "idle"; this.wantReconnect = true; this.mirror = false;
    }
    static supported() { return !!(global.navigator && navigator.bluetooth); }
    setState(s, info) { this.state = s; this.onstate(s, info); }
    log(m) { this.onlog(m); }

    async connect() {
      if (!Glasses.supported()) throw Object.assign(new Error("Ce navigateur ne gère pas le Bluetooth web"), { code: "unsupported" });
      this.setState("searching");
      try {
        this.dev = await navigator.bluetooth.requestDevice({ filters: [{ namePrefix: "GLASSES" }], optionalServices: [SERVICE] });
      } catch (e) {
        // une DOMException a un « code » en lecture seule : on renvoie une erreur neuve avec notre propre code
        this.setState("idle"); const err = new Error(e.message || "Bluetooth indisponible"); err.code = e.name === "NotFoundError" ? "cancelled" : "scan"; throw err;
      }
      this.setState("found", this.dev.name);
      this.dev.addEventListener("gattserverdisconnected", () => this.onDisconnect());
      await this.openGatt();
      return this.dev.name;
    }
    async openGatt() {
      this.setState("connecting", this.dev.name);
      const server = await this.dev.gatt.connect();
      const svc = await server.getPrimaryService(SERVICE);
      for (const k of Object.keys(CH)) this.ch[k] = await svc.getCharacteristic(CH[k]);
      await this.ch.notify.startNotifications();
      this.ch.notify.addEventListener("characteristicvaluechanged", (e) => this.onNotify(new Uint8Array(e.target.value.buffer)));
      this.last = null; this.inDIY = false;
      this.setState("connected", this.dev.name);
      this.log("Connecté à " + this.dev.name);
    }
    async onDisconnect() {
      this.log("Déconnecté");
      if (!this.wantReconnect || !this.dev) { this.setState("idle"); return; }
      this.setState("reconnecting", this.dev.name);
      for (let i = 0; i < 5; i++) {
        await sleep(1000 * (i + 1));
        try { await this.openGatt(); return; } catch (e) { this.log("Reconnexion ratée (" + (i + 1) + "/5)"); }
      }
      this.setState("failed", "Lunettes hors de portée ou éteintes");
    }
    disconnect() { this.wantReconnect = false; if (this.dev && this.dev.gatt.connected) this.dev.gatt.disconnect(); this.setState("idle"); }
    get connected() { return !!(this.dev && this.dev.gatt && this.dev.gatt.connected && this.ch.cmd); }

    async onNotify(raw) {
      // Réponses chiffrées : [longueur][texte ASCII] une fois déchiffrées (DATSOK, DATCPOK, ERROR).
      let txt = "";
      try {
        for (let i = 0; i + 16 <= raw.length; i += 16) { const p = await decryptBlock(raw.slice(i, i + 16)); const n = p[0]; if (n > 0 && n < 16) txt += String.fromCharCode(...p.slice(1, 1 + n)).replace(/\0+$/, ""); }
      } catch (e) {}
      if (!/^[\x20-\x7e]+$/.test(txt)) this.log("Réponse brute (" + raw.length + " octets) : " + hex(raw));
      if (!/^[\x20-\x7e]+$/.test(txt)) txt = String.fromCharCode(...raw).replace(/[^\x20-\x7e]/g, "");
      this.log("Réponse : " + (txt || hex(raw)));
      const w = this.replyWaiters.shift(); if (w) w(txt || hex(raw));
    }
    waitReply(ms = 2500) { return new Promise((res) => { const t = setTimeout(() => res("TIMEOUT"), ms); this.replyWaiters.push((v) => { clearTimeout(t); res(v); }); }); }

    /* Toutes les écritures passent par une file : jamais deux en même temps. */
    write(chKey, plain, withResponse = false) {
      const job = this.queue.then(async () => {
        if (!this.connected) throw new Error("pas connecté");
        const enc = await encryptBlock(plain);
        const c = this.ch[chKey];
        if (withResponse || !c.writeValueWithoutResponse) await c.writeValueWithResponse(enc);
        else await c.writeValueWithoutResponse(enc);
        await sleep(this.pacing);
      });
      this.queue = job.catch(() => {});
      return job;
    }
    command(f) { return this.write("cmd", f, true); }

    async ensureDIY() { if (!this.inDIY) { await this.command(CMD.enterDIY()); await this.command(CMD.ledOn()); this.inDIY = true; this.last = null; } }

    /* Affiche une image 24 × 9 (valeurs 0-3, y = 0 en haut). N'envoie que les colonnes qui changent. */
    async showFrame(px, full = false) {
      await this.ensureDIY();
      if (this.mirror) px = mirrorPx(px);
      const words = Array.from({ length: COLS }, (_, c) => columnWord(px, c));
      const todo = words.map((w, c) => c).filter((c) => full || !this.last || this.last[c] !== words[c]);
      for (let i = 0; i < todo.length; i++) {
        const c = todo[i];
        await this.write("live", CMD.column(c, words[c]), i === todo.length - 1);
      }
      this.last = words;
      return todo.length;
    }
    async saveFrame() { await this.command(CMD.saveDIY()); this.inDIY = false; }

    /* Texte long qui défile tout seul dans les lunettes (envoi DATS type 1). */
    async uploadText(cols7, { flip = true, mode = 2, dir = 0, speed = null, speedFirst = false, mirror = this.mirror } = {}) {
      this.inDIY = false;
      if (speed != null && speedFirst) await this.command(CMD.speed(speed));
      const data = textPayload(cols7, flip, mirror);
      const okP = this.waitReply(); // on écoute avant d'envoyer : la réponse arrive très vite
      await this.command(CMD.dats(1, data.length));
      const ok = await okP;
      if (!/DATSOK/.test(ok)) this.log("Pas de DATSOK (" + ok + "), on continue quand même");
      for (let i = 0; i < data.length; i += 15) {
        const chunk = data.slice(i, i + 15);
        const blk = new Uint8Array(16); blk[0] = chunk.length; blk.set(chunk, 1);
        await this.write("dats", blk);
        await sleep(32); // l'appli d'origine attend 50 ms au total entre deux blocs
      }
      const doneP = this.waitReply();
      await this.command(CMD.datcp());
      const done = await doneP;
      await this.command(CMD.mode(mode, dir));
      if (speed != null && !speedFirst) await this.command(CMD.speed(speed)); // par défaut : vitesse après le lancement
      return done;
    }
    async brightness(n) { await this.command(CMD.light(n)); }
    async speed(n) { await this.command(CMD.speed(n)); }
    /* LEDOFF ne fait rien sur notre modèle (testé) : « éteindre » = écran noir en mode DIY. */
    async off() { this.last = null; await this.showFrame(new Array(COLS * ROWS).fill(0), true); }
    async builtinAnim(n) { this.inDIY = false; await this.command(CMD.anim(n)); }
    async builtinImage(n) { this.inDIY = false; await this.command(CMD.image(n)); }
  }

  global.ClignoteBLE = { Glasses, CMD, frame, encryptBlock, decryptBlock, mirrorPx, columnWord, textPayload, alive, hex, fromHex, ROWS, COLS, DEAD };
})(typeof window !== "undefined" ? window : globalThis);
